import { ActivitySpinnerSize, ActivitySpinnerWidget, ListItem, LocallyFilteringList,TabStyles } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { cssClass } from '../../style';

export type EnableAccelerationDialogBodyProps = {
  readonly prompt: string;
  readonly needsSchedule: boolean;
  readonly getQuery: () => ResourceQuery<IScheduleInfo>;
};
export type EnableAccelerationDialogState = {
  readonly schedule: IScheduleInfo;
};
export type PropsForConfirmActionDialogBody = ReactActionDialogBodyProps<EnableAccelerationDialogBodyProps, EnableAccelerationDialogState>;

export function EnableAccelerationDialogBody(props: PropsForConfirmActionDialogBody): JSX.Element {
  const [items, setItems] = React.useState([]);
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  const onSelectSchedule = React.useCallback(
    (value: string) => {
      const item = items.find((item) => item.value === value);
      if (item) {
        props.updateDialogState({ schedule: item.schedule });
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.updateDialogState, items]
  );

  React.useEffect(
    () => {
      async function fetchSchedules() {
        try {
          const scheduleResult = await SliceableHelper.fetchAll(props.getQuery());
          const items: ListItem[] = scheduleResult.items.map((schedule) => ({
            content: createItemElement(schedule.name),
            value: schedule.name,
            testId: 'list-item-' + schedule.id,
            schedule: schedule,
          }));

          setShowSpinner(false);
          setItems(items);
        } catch (e) {
          setShowSpinner(false);
          setIsError(true);
        }
      }

      fetchSchedules();
      return;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* eslint-disable jsx-a11y/no-autofocus */
  return (
    <>
      <div className={prompt}>
        {props.prompt}
      </div>
      {props.needsSchedule && <div style={{ height: 300 }}>
        <LocallyFilteringList
          items={items}
          onSelect={onSelectSchedule}
          searchPlaceholder={Messages.search()}
          testId="filtering-list-enable-acceleration"
          style={{height: '100%', maxHeight:'300px', width: 'auto', border: '1px solid #b2b2b2'}}
          autoFocus={true}
          inputTestId="owner-picker-filter"
          listTestId="owner-picker-list"
        >
          {!showSpinner && (items.length === 0 || isError) &&
            <div className={noResults} data-tb-test-id="set-owner-no-results">
              {Messages.noMatchingResults()}
            </div>
          }
          {showSpinner &&
            <div className={spinner}>
              <ActivitySpinnerWidget
                spinnerSize={ActivitySpinnerSize.Large}
                testId={'owner-picker'}
              />
            </div>
          }
        </LocallyFilteringList>
      </div>
      }
    </>
  );
}

const createItemElement = (scheduleDisplayname: string) =>
  (<span style={{fontSize: '12px', lineHeight: '27px'}}>
    {scheduleDisplayname}
  </span>);

const prompt = cssClass('enable-workbook-acceleration-dialog', 'prompt', {
  fontSize: TabStyles.Typography.Sizing.Large,
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const spinner = cssClass('enable-workbook-acceleration-dialog', 'spinner', {
  marginTop: 18,
  textAlign: 'center'
});

const noResults = cssClass('enable-workbook-acceleration-dialog', 'no-results', {
  fontSize: '15px',
  marginTop: 18,
  padding: 6,
  lineHeight: '23px',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
